import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { LoginRequest, LoginApiResponse, AuthResult } from '../models/auth.model';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AuthApiService {
    private readonly http = inject(HttpClient);

    private domain: string;
    private adminDomain: string;
    private clientID: string;
    private audience: string;
    private redirectUri: string;
    private responseType: string;
    private scope: string;

    constructor() {
        this.domain = environment.hubx_auth_domain || '';
        this.adminDomain = environment.hubx_auth_apiUrl || '';
        this.clientID = environment.hubx_auth_clientId || '';
        this.responseType = 'token id_token';
        this.scope = environment.hubx_auth_scope || '';
        this.redirectUri = environment.hubx_auth_RedirectUrl || '';
        this.audience = ''; // not used in password grant
    }

    login(username: string, password: string, realm: string): Observable<AuthResult> {
        const request: LoginRequest = {
            realm,
            username,
            password
        };

        return from(this.requestToken(request)).pipe(
            map(response => this.mapToAuthResult(response))
        );
    }

    private requestToken(options: LoginRequest): Promise<LoginApiResponse> {
        const params = new HttpParams()
            .set('audience', this.audience)
            .set('client_id', this.clientID)
            .set('username', options.realm + '\\' + options.username)
            .set('grant_type', 'password')
            .set('password', options.password);

        const url = this.domain + 'connect/token';
        return new Promise((resolve, reject) => {
            this.httpRequest(url, 'POST', params, resolve, reject);
        });
    }

    private httpRequest(
        path: string,
        type: 'GET' | 'POST' | 'DELETE' | 'PATCH',
        params: HttpParams,
        resolve: Function,
        reject: Function
    ): void {
        const url = path;
        const headers = new HttpHeaders().append('Content-Type', 'application/x-www-form-urlencoded');

        if (type === 'GET') {
            this.http.get<LoginApiResponse>(url, { headers, params })
                .subscribe({
                    next: response => resolve(response),
                    error: error => this.httpResponseError(error, path, type, params, resolve, reject)
                });
        } else if (type === 'POST') {
            this.http.post<LoginApiResponse>(url, params, { headers })
                .subscribe({
                    next: response => resolve(response),
                    error: error => this.httpResponseError(error, path, type, params, resolve, reject)
                });
        } else if (type === 'DELETE') {
            this.http.delete<LoginApiResponse>(url, { headers })
                .subscribe({
                    next: response => resolve(response),
                    error: error => this.httpResponseError(error, path, type, params, resolve, reject)
                });
        } else if (type === 'PATCH') {
            this.http.patch<LoginApiResponse>(url, params, { headers })
                .subscribe({
                    next: response => resolve(response),
                    error: error => this.httpResponseError(error, path, type, params, resolve, reject)
                });
        }
    }

    /**
     * Handle HTTP response errors
     */
    private httpResponseError(
        error: any,
        path: string,
        type: string,
        params: HttpParams,
        resolve: Function,
        reject: Function
    ): void {
        if (error.status === 401) {
            console.error('Unauthorized - CSRF token expired:', error.error);
        }
        reject(error);
    }

    /**
     * map Auth API response to AuthResult
     */
    private mapToAuthResult(response: LoginApiResponse): AuthResult {
        return {
            accessToken: response.access_token,
            expiresIn: response.expires_in,
            tokenType: response.token_type,
            refreshToken: null,
            idToken: null
        };
    }

    /**
     * Change/Reset password
     */
    changePassword(email: string, connection: string): Observable<any> {
        const params = {
            userName: connection + '\\' + email,
            clientId: this.clientID
        };

        const url = this.adminDomain + 'api/Users/forgotreqs';

        return this.http.post(url, params).pipe(
            map(response => {
                const result = JSON.parse(JSON.stringify(response));
                return result;
            }),
            catchError(error => {
                console.error('Change password error:', error);
                return throwError(() => error);
            })
        );
    }
}